/*
 * bundle-sync.js
 * Keeps "free bundle" child line items in sync with their parent line.
 *
 * Free children are added at add-to-cart (see snippets/buy-buttons.liquid) and carry a
 * line-item property `_bundle_parent` = the parent variant id; the parent line carries
 * `_bundle` = "true". On every cart update this reads /cart.js, forces each child's
 * quantity to match its parent's, removes children whose parent is gone, and collapses
 * duplicate child lines — then re-renders the cart sections the Dawn way.
 *
 * No discount logic: the $0 price comes from the child products being priced $0.00.
 */
(() => {
  const DEBOUNCE_MS = 150;

  const postConfig = () =>
    typeof fetchConfig === 'function'
      ? fetchConfig()
      : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };

  class BundleSync {
    constructor() {
      this.syncing = false;
      this.timer = null;

      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        // Ignore events we might emit ourselves; everything else schedules a pass.
        if (event && event.source === 'bundle-sync') return;
        this.schedule();
      });

      // Handle a cart that already contains a bundle on initial page load.
      this.schedule();
    }

    schedule() {
      clearTimeout(this.timer);
      // Run just after Dawn finishes its own DOM update.
      this.timer = setTimeout(() => this.run(), DEBOUNCE_MS);
    }

    async run() {
      if (this.syncing) return;
      this.syncing = true;

      try {
        const cart = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
          r.json()
        );
        const changes = this.computeChanges(cart.items || []);
        if (!changes.length) return;

        // Apply precise per-line changes; /cart/change.js by line key does not re-broadcast
        // cartUpdate, so this cannot loop back into us.
        for (const change of changes) {
          await fetch(`${routes.cart_change_url}`, {
            ...postConfig(),
            body: JSON.stringify({ id: change.key, quantity: change.quantity }),
          });
        }

        await this.rerender();
      } catch (e) {
        console.error('[bundle-sync]', e);
      } finally {
        this.syncing = false;
      }
    }

    /**
     * Returns a list of { key, quantity } changes to enforce the bundle rules.
     * @param {Array} items - cart.items from /cart.js
     */
    computeChanges(items) {
      // Parent quantity, keyed by parent variant id (lines flagged with the _bundle property).
      const parentQty = {};
      items.forEach((item) => {
        if (item.properties && item.properties._bundle) {
          parentQty[item.variant_id] = (parentQty[item.variant_id] || 0) + item.quantity;
        }
      });

      const changes = [];
      const kept = {}; // `${variantId}|${parentId}` -> already kept one line for this child

      items.forEach((item) => {
        const parentId = item.properties && item.properties._bundle_parent;
        if (!parentId) return; // not a bundle child

        const target = parentQty[parentId] || 0;
        const groupKey = `${item.variant_id}|${parentId}`;

        if (target === 0) {
          // Parent is gone -> remove the child.
          changes.push({ key: item.key, quantity: 0 });
          return;
        }
        if (kept[groupKey]) {
          // Duplicate child line for the same parent -> collapse it.
          changes.push({ key: item.key, quantity: 0 });
          return;
        }
        kept[groupKey] = true;
        if (item.quantity !== target) {
          changes.push({ key: item.key, quantity: target });
        }
      });

      return changes;
    }

    async rerender() {
      // Pull the post-change cart so we can toggle empty states the way Dawn does.
      const cart = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
        r.json()
      );
      const isEmpty = cart.item_count === 0;
      const parse = (t) => new DOMParser().parseFromString(t, 'text/html');

      // Cart drawer (always present in the layout).
      if (document.querySelector('cart-drawer-items')) {
        const doc = parse(await fetch(`${routes.cart_url}?section_id=cart-drawer`).then((r) => r.text()));
        ['cart-drawer-items', '.cart-drawer__footer'].forEach((selector) => {
          const target = document.querySelector(selector);
          const source = doc.querySelector(selector);
          if (target && source) target.replaceWith(source);
        });
        document.querySelector('cart-drawer')?.classList.toggle('is-empty', isEmpty);
      }

      // Main cart page — items.
      if (document.getElementById('main-cart-items')) {
        const doc = parse(await fetch(`${routes.cart_url}?section_id=main-cart-items`).then((r) => r.text()));
        const target = document.querySelector('#main-cart-items .js-contents');
        const source = doc.querySelector('.js-contents');
        if (target && source) target.innerHTML = source.innerHTML;
        document.querySelector('cart-items')?.classList.toggle('is-empty', isEmpty);
      }

      // Main cart page — footer (totals / checkout). Without this the total stays stale
      // after a bundle line is removed, making it look like nothing was removed.
      if (document.getElementById('main-cart-footer')) {
        const doc = parse(await fetch(`${routes.cart_url}?section_id=main-cart-footer`).then((r) => r.text()));
        const target = document.querySelector('#main-cart-footer .js-contents');
        const source = doc.querySelector('.js-contents');
        if (target && source) target.innerHTML = source.innerHTML;
        document.getElementById('main-cart-footer').classList.toggle('is-empty', isEmpty);
      }

      // Cart icon count bubble.
      try {
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble) {
          const doc = parse(await fetch(`${routes.cart_url}?section_id=cart-icon-bubble`).then((r) => r.text()));
          const source = doc.querySelector('.shopify-section') || doc.body.firstElementChild;
          if (source) bubble.innerHTML = source.innerHTML;
        }
      } catch (e) {
        console.error('[bundle-sync] bubble', e);
      }
    }
  }

  const init = () => new BundleSync();

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  }
})();
